import { gql } from "@apollo/client";
import { client } from "../apolloClient";
import { useQuery, useMutation } from "@apollo/client";

/**
 * useClientPocApi custom hook for client POC API operations.
 * - Provides CRUD operations for client POCs using Apollo Client and GraphQL.
 * - Handles cache updates for client POC queries and mutations.
 * - Exposes helpers for reading/writing/updating client POCs in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting client POCs.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const CLIENT_POCS_QUERIES = {
  // Query to fetch all client POCs
  GET_CLIENT_POCS: gql`
    query GetClientPocs {
      GetClientPocs {
        client_poc_id
        client_id
        poc_designation
        poc_country_id
        poc_name
        poc_email
        poc_mobile
        poc_business_unit
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to add a new client POC
  ADD_CLIENT_POC: gql`
    mutation AddClientPoc(
      $clientId: Int!
      $pocDesignation: String
      $pocName: String!
      $pocEmail: String!
      $pocMobile: String
      $pocBusinessUnit: String
      $pocCountryId: Int
    ) {
      AddClientPoc(
        client_id: $clientId
        poc_designation: $pocDesignation
        poc_name: $pocName
        poc_email: $pocEmail
        poc_mobile: $pocMobile
        poc_business_unit: $pocBusinessUnit
        poc_country_id: $pocCountryId
      ) {
        client_poc_id
        client_id
        poc_designation
        poc_country_id
        poc_name
        poc_email
        poc_mobile
        poc_business_unit
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing client POC
  UPDATE_CLIENT_POC: gql`
    mutation UpdateClientPoc(
      $clientPocId: ID!
      $clientId: Int!
      $pocDesignation: String
      $pocName: String
      $pocEmail: String
      $pocMobile: String
      $pocBusinessUnit: String
      $pocCountryId: Int
    ) {
      UpdateClientPoc(
        client_poc_id: $clientPocId
        client_id: $clientId
        poc_designation: $pocDesignation
        poc_name: $pocName
        poc_email: $pocEmail
        poc_mobile: $pocMobile
        poc_business_unit: $pocBusinessUnit
        poc_country_id: $pocCountryId
      ) {
        client_poc_id
        client_id
        poc_designation
        poc_country_id
        poc_name
        poc_email
        poc_mobile
        poc_business_unit
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a client POC
  DELETE_CLIENT_POC: gql`
    mutation DeleteClientPoc($clientPocId: ID!) {
      DeleteClientPoc(client_poc_id: $clientPocId) {
        client_poc_id
        client_id
        poc_designation
        poc_name
        poc_email
        poc_mobile
        is_active
        created_on
        updated_on
      }
    }
  `,
};

// Cache operations for client POCs
const cacheOperations = {
  /**
   * Writes the provided client POCs array to Apollo Client cache.
   * @param {Array} clientPocs - Array of client POC objects.
   */
  writeClientPocs: (clientPocs) => {
    if (!clientPocs) return;
    client.writeQuery({
      query: CLIENT_POCS_QUERIES.GET_CLIENT_POCS,
      data: {
        GetClientPocs: clientPocs,
      },
    });
  },

  /**
   * Reads the client POCs array from Apollo Client cache.
   * @returns {Array} Array of client POC objects or empty array if not found.
   */
  readClientPocs: () => {
    try {
      const { GetClientPocs } =
        client.readQuery({
          query: CLIENT_POCS_QUERIES.GET_CLIENT_POCS,
        }) || {};
      return GetClientPocs || [];
    } catch {
      return [];
    }
  },

  /**
   * Updates a client POC in the Apollo Client cache.
   * @param {Object} updatedClientPoc - The updated client POC object.
   */
  updateClientPocsInCache: (updatedClientPoc) => {
    const existingClientPocs = cacheOperations.readClientPocs();
    const updatedClientPocs = existingClientPocs.map((clientPoc) =>
      clientPoc.client_poc_id === updatedClientPoc.client_poc_id
        ? updatedClientPoc
        : clientPoc
    );
    cacheOperations.writeClientPocs(updatedClientPocs);
  },
};

export const useClientPocApi = () => {
  const [addClientPocMutation] = useMutation(
    CLIENT_POCS_QUERIES.ADD_CLIENT_POC
  );
  const [updateClientPocMutation] = useMutation(
    CLIENT_POCS_QUERIES.UPDATE_CLIENT_POC
  );

  /**
   * Fetches all client POCs from the backend.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getClientPocs = async () => {
    try {
      const { data } = await client.query({
        query: CLIENT_POCS_QUERIES.GET_CLIENT_POCS,
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetClientPocs };
    } catch (error) {
      console.error("Error fetching client pocs:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a client POC by its ID.
   * @param {number|string} client_poc_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getClientPocById = async (client_poc_id) => {
    try {
      const { data } = await client.query({
        query: CLIENT_POCS_QUERIES.GET_CLIENT_POC_BY_ID,
        variables: { client_poc_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetClientPocById };
    } catch (error) {
      console.error("Error fetching client poc:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new client POC.
   * @param {Object} formData - Client POC form data.
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createClientPoc = async (formData) => {
    try {
      const { data, errors } = await addClientPocMutation({
        variables: {
          clientId: parseInt(formData.client_id),
          pocDesignation: formData.poc_designation,
          pocEmail: formData.poc_email,
          pocName: formData.poc_name,
          pocMobile: formData.poc_mobile,
          pocBusinessUnit: formData.poc_business_unit,
          pocCountryId: parseInt(formData.poc_country_id),
          isActive: formData.is_active,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.AddClientPoc) {
        throw new Error("Failed to create client poc");
      }

      return {
        success: true,
        data: data.AddClientPoc,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Updates an existing client POC.
   * @param {number|string} clientPocId
   * @param {Object} formData - Client POC form data.
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateClientPoc = async (clientPocId, formData) => {
    try {
      const { data, errors } = await updateClientPocMutation({
        variables: {
          clientPocId: parseInt(clientPocId),
          clientId: parseInt(formData.client_id),
          pocDesignation: formData.poc_designation,
          pocEmail: formData.poc_email,
          pocName: formData.poc_name,
          pocMobile: formData.poc_mobile,
          pocBusinessUnit: formData.poc_business_unit,
          pocCountryId: parseInt(formData.poc_country_id),
          isActive: formData.is_active,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.UpdateClientPoc) {
        throw new Error("Failed to update Client Poc");
      }

      return {
        success: true,
        data: data.UpdateClientPoc,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Deletes a client POC by its ID.
   * @param {number|string} client_poc_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteClientPoc = async (client_poc_id) => {
    try {
      const { data } = await client.mutate({
        mutation: CLIENT_POCS_QUERIES.DELETE_CLIENT_POC,
        variables: { clientPocId: client_poc_id },
      });

      const existingClientPocs = cacheOperations.readClientPocs();
      cacheOperations.writeClientPocs(
        existingClientPocs.filter(
          (clientPoc) => clientPoc.client_poc_id !== client_poc_id
        )
      );

      return { success: true, data: data.DeleteClientPoc };
    } catch (error) {
      console.error("Error deleting Client poc:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getClientPocs,
    getClientPocById,
    createClientPoc,
    updateClientPoc,
    deleteClientPoc,
    cacheOperations,
  };
};
